import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { checkAuth, login, ApiError } from '../lib/api'
import { hydrateTuningState } from '../store/tuningStore'
import { ImportLocalDataBanner } from './ImportLocalDataBanner'

const DISMISS_KEY = 'microtonal_import_dismissed'

export const AuthGate = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking')
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showImportBanner, setShowImportBanner] = useState(false)

  useEffect(() => {
    checkAuth().then((ok) => {
      setStatus(ok ? 'authenticated' : 'unauthenticated')
      if (ok) {
        hydrateTuningState()
        setShowImportBanner(!localStorage.getItem(DISMISS_KEY))
      }
    })
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await login(pin)
      setStatus('authenticated')
      hydrateTuningState()
      setShowImportBanner(!localStorage.getItem(DISMISS_KEY))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erreur de connexion')
      setPin('')
    } finally {
      setSubmitting(false)
    }
  }

  const dismissImportBanner = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setShowImportBanner(false)
  }

  if (status === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        Chargement...
      </div>
    )
  }

  if (status === 'unauthenticated') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-xs space-y-4 p-8 rounded-2xl border bg-card shadow-xl"
        >
          <div className="text-center space-y-1">
            <div className="text-3xl">🔒</div>
            <h1 className="text-lg font-semibold">Accès protégé</h1>
            <p className="text-sm text-muted-foreground">Entrez le code PIN</p>
          </div>
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-xl text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring"
            maxLength={12}
          />
          {error && <p className="text-sm text-red-600 text-center">{error}</p>}
          <button
            type="submit"
            disabled={submitting || pin.length === 0}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold disabled:opacity-50"
          >
            {submitting ? 'Vérification...' : 'Déverrouiller'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <>
      {showImportBanner && <ImportLocalDataBanner onDone={dismissImportBanner} />}
      {children}
    </>
  )
}
